/**
 * White-label tenant theming seam (client-surface half).
 *
 * AVOWAL is sold to attorneys as infrastructure; the AVOWAL brand is invisible
 * to end-clients (MASTER_DESIGN_PLAN §0.5, LOCKED #4/#5). Each attorney/firm is
 * a TENANT with a branded, configurable client interface: own brand/colors, a
 * named AI assistant with a persona, and an AI-disclosure level that governs how
 * much of the AI is surfaced to the client.
 *
 * This provider is the CLIENT-SURFACE half of that: it
 *   (a) themes the client portal via CSS variables from the tenant palette,
 *   (b) exposes the tenant's *named* AI assistant so client copy can say the
 *       attorney's assistant name instead of "ELARIA"/"the service", and
 *   (c) respects the disclosureLevel (transparent → assisted → concierge →
 *       invisible) to gate how much AI is surfaced in client-facing copy.
 *
 * It reads the tenant from the session (`/auth/me` → `me.tenant`) when the API
 * supplies it, and otherwise falls back to a well-typed STUB default so the app
 * builds and renders today. The attorney-facing tenant-config admin UI is a
 * later increment; the schema shape here mirrors the planned `tenant_config`.
 */
import {
  createContext, useContext, useEffect, useMemo, type ReactNode, type CSSProperties,
} from 'react';
import { useMe } from './session';

export type DisclosureLevel = 'transparent' | 'assisted' | 'concierge' | 'invisible';

/**
 * A single tenant-configured intake question (LOCKED #5 — per-tenant intake
 * question set). The public intake→quote flow renders whatever set the tenant
 * configures; when the API serves none, the flow falls back to
 * DEFAULT_INTAKE_QUESTIONS so it always has a working front door.
 *
 * `name` is the field key posted to the leads endpoint. Three names are
 * load-bearing for the quote (they drive the rate-card anchor + summary):
 * `email`, `jurisdiction`, and `matterType`. A configured set SHOULD include
 * them; the flow degrades gracefully if it doesn't (no anchor, plain summary).
 */
export interface IntakeQuestion {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'email' | 'select';
  required?: boolean;
  placeholder?: string;
  help?: string;
  options?: { value: string; label: string }[];
}

/** The stock question set — mirrors the fields the shipped intake modal used. */
export const DEFAULT_INTAKE_QUESTIONS: IntakeQuestion[] = [
  { name: 'email', label: 'Email', type: 'email', required: true, placeholder: 'you@example.com' },
  { name: 'jurisdiction', label: 'State / jurisdiction', type: 'text', required: true, placeholder: 'e.g. Connecticut, Superior Court' },
  {
    name: 'matterType', label: 'Matter type', type: 'select', required: true,
    options: [
      { value: 'family', label: 'Family / dissolution' },
      { value: 'contract', label: 'Contract dispute' },
      { value: 'small_claims', label: 'Small claims' },
      { value: 'landlord_tenant', label: 'Landlord / tenant' },
      { value: 'employment', label: 'Employment' },
      { value: 'estate', label: 'Estate / probate' },
      { value: 'appellate', label: 'Appellate' },
      { value: 'other', label: 'Other' },
    ],
  },
  { name: 'summary', label: 'One-line summary', type: 'textarea', required: true, placeholder: 'What do you need help with? Plain English is fine.' },
];

/** Brand palette overrides. Each maps onto an app CSS token (see themeVars). */
export interface TenantColors {
  /** Primary accent — overrides `--cyan`. */
  primary?: string;
  /** Dim primary (focus rings/fills) — overrides `--cyan-dim`. */
  primaryDim?: string;
  /** Primary glow (shadows) — overrides `--cyan-glow`. */
  primaryGlow?: string;
  /** Secondary accent (gravitas) — overrides `--gold`. */
  accent?: string;
}

export interface TenantBranding {
  /** Firm/tenant name shown in the client chrome. Defaults to 'AVOWAL'. */
  displayName?: string;
  /** Logo URL (rendered where the wordmark is today). */
  logo?: string | null;
  colors?: TenantColors;
}

export interface TenantAiAssistant {
  /** The attorney's named assistant, e.g. "Ava". Defaults to 'ELARIA'. */
  name?: string;
  avatar?: string | null;
  persona?: string;
  tone?: string;
  /** How much AI to surface to the client. Defaults to 'transparent'. */
  disclosureLevel?: DisclosureLevel;
}

/**
 * The per-tenant config. A superset of what this increment consumes — the
 * intake/workflow fields are carried so the type matches the planned
 * `tenant_config` shape even though their UIs land later.
 */
export interface TenantConfig {
  id?: string;
  domain?: string;
  branding?: TenantBranding;
  aiAssistant?: TenantAiAssistant;
  intake?: { questions?: IntakeQuestion[] };
  workflow?: { rules?: unknown };
}

/** Well-typed STUB default — reproduces today's AVOWAL-branded behavior. */
export const DEFAULT_TENANT: TenantConfig = {
  id: 'avowal-default',
  branding: { displayName: 'AVOWAL', logo: null, colors: {} },
  aiAssistant: { name: 'ELARIA', disclosureLevel: 'transparent' },
};

export interface TenantContextValue {
  tenant: TenantConfig;
  /** Firm/tenant display name for the client chrome. Defaults to 'AVOWAL'. */
  displayName: string;
  /** The tenant's named AI assistant. Defaults to 'ELARIA'. */
  assistantName: string;
  /** How much AI may be surfaced to the client. */
  disclosureLevel: DisclosureLevel;
  /** Whether the assistant may be named/surfaced in client copy (false when 'invisible'). */
  surfaceAssistant: boolean;
  /** Assistant name to render in client copy, or null when it must stay invisible. */
  clientAssistantName: string | null;
}

function deriveValue(tenant: TenantConfig): TenantContextValue {
  const displayName = tenant.branding?.displayName?.trim() || 'AVOWAL';
  const assistantName = tenant.aiAssistant?.name?.trim() || 'ELARIA';
  const disclosureLevel = tenant.aiAssistant?.disclosureLevel ?? 'transparent';
  const surfaceAssistant = disclosureLevel !== 'invisible';
  return {
    tenant,
    displayName,
    assistantName,
    disclosureLevel,
    surfaceAssistant,
    clientAssistantName: surfaceAssistant ? assistantName : null,
  };
}

/** Map the tenant palette onto the app's CSS tokens (only set what's provided). */
function themeVars(colors: TenantColors | undefined): CSSProperties {
  const vars: Record<string, string> = {};
  if (colors?.primary) vars['--cyan'] = colors.primary;
  if (colors?.primaryDim) vars['--cyan-dim'] = colors.primaryDim;
  if (colors?.primaryGlow) vars['--cyan-glow'] = colors.primaryGlow;
  if (colors?.accent) vars['--gold'] = colors.accent;
  return vars as CSSProperties;
}

const TenantContext = createContext<TenantContextValue>(deriveValue(DEFAULT_TENANT));

/**
 * Provides tenant theming to the client portal. Reads `me.tenant` from the
 * session when present; otherwise uses the STUB default. An explicit `tenant`
 * prop overrides both (useful for previews/tests). Themes descendants by setting
 * the tenant palette as CSS variables on a `display: contents` wrapper, so no
 * layout changes and the default (AVOWAL) values are a visual no-op.
 */
export function TenantThemeProvider({
  children, tenant: override,
}: { children: ReactNode; tenant?: TenantConfig }) {
  const { me } = useMe();
  const tenant = override ?? me?.tenant ?? DEFAULT_TENANT;
  const value = useMemo(() => deriveValue(tenant), [tenant]);
  const style = useMemo<CSSProperties>(
    () => ({ display: 'contents', ...themeVars(tenant.branding?.colors) }),
    [tenant],
  );
  // PWA per-tenant branding override "where wired" (mobile-native.md §5a): reflect
  // the tenant's primary color into the browser/OS theme-color and set the
  // installed-app title to the firm's display name. The install NAME/icons stay
  // generic AVOWAL in the manifest; only these runtime hints are tenant-branded.
  // No case name or dollar amount is ever written here (no-preview posture).
  useEffect(() => {
    const primary = tenant.branding?.colors?.primary;
    if (primary) {
      document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
        .forEach((m) => { m.content = primary; });
    }
    const title = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    if (title && value.displayName) title.content = value.displayName;
  }, [tenant, value.displayName]);
  return (
    <TenantContext.Provider value={value}>
      <div style={style} data-tenant={value.tenant.id ?? 'avowal-default'}>{children}</div>
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  return useContext(TenantContext);
}

/**
 * The tenant's configured intake question set, or the stock default. Kept here
 * (not on the context value) because the PUBLIC intake→quote flow resolves its
 * tenant by domain, not from the session — see fetchPublicTenant().
 */
export function tenantIntakeQuestions(tenant: TenantConfig): IntakeQuestion[] {
  const qs = tenant.intake?.questions;
  return qs && qs.length > 0 ? qs : DEFAULT_INTAKE_QUESTIONS;
}

/**
 * Resolve the PUBLIC tenant config for the white-label front door.
 *
 * The end-client reaches the intake→quote flow on the ATTORNEY's own branded
 * domain, so the tenant is resolved by host, not by an authenticated session
 * (the client isn't logged in yet). This hits a public, unauthenticated
 * endpoint (GET /api/tenant) that returns the tenant's branding + named
 * assistant + disclosure level + intake question set for the current host.
 *
 * The endpoint is a sibling-repo build shipping in parallel; this ALWAYS
 * degrades to DEFAULT_TENANT so the public flow renders today (AVOWAL-branded,
 * ELARIA, stock questions) and silently upgrades once the API serves a tenant.
 */
export async function fetchPublicTenant(): Promise<TenantConfig> {
  try {
    const res = await fetch('/api/tenant', { headers: { Accept: 'application/json' } });
    if (!res.ok) return DEFAULT_TENANT;
    const body = await res.json().catch(() => ({}));
    const tenant = (body?.tenant ?? body) as TenantConfig | undefined;
    if (!tenant || typeof tenant !== 'object') return DEFAULT_TENANT;
    return tenant;
  } catch {
    return DEFAULT_TENANT;
  }
}
